import type { PropertySubtype } from '../uc/categories'
// Jednosmerne: uc/attributes.ts je čistý číselník a odtiaľto neimportuje nič
// (prevody závislé na doméne sú v uc/uc-export.ts). Import drž `import type` —
// runtime hodnota z číselníka sem nepatrí.
import type {
  UcEquipType,
  UcPosition,
  UcTerritory,
  UcHouseType,
  UcGroundType,
  UcReadyToBuild,
  UcHeatingSystem,
  UcOrientation,
  UcBuildingType,
  UcParkingType,
  UcWater,
  UcElectric,
  UcSewage,
  UcGas,
} from '../uc/attributes'

export const PROPERTY_TRANSACTION_TYPES = [
  'SALE',
  'RENT',
  'AUCTION',
  'LEASE',
  'PURCHASE',
  'EXCHANGE',
] as const

export type PropertyTransactionType = (typeof PROPERTY_TRANSACTION_TYPES)[number]

/**
 * Skupina kategórií podľa UC taxonómie (nehnutelnosti.sk). Konkrétny druh nesie
 * `PropertySubtype` z `../uc/categories` — platí invariant
 * `subtypeGroup(propertySubtype) === propertyType`.
 */
export const PROPERTY_TYPES = [
  'GARAGES_AND_PARKING',
  'APARTMENTS',
  'HOUSES',
  'LANDS',
  'SPACES',
  'OBJECTS',
] as const

export type PropertyType = (typeof PROPERTY_TYPES)[number]

export const PROPERTY_DEAL_TYPES = ['EXCLUSIVE', 'NON_EXCLUSIVE', 'OFF_MARKET'] as const
/** Typ ponuky (zmluvný vzťah s vlastníkom) — parita s Prisma enumom PropertyDealType. */
export type PropertyDealType = (typeof PROPERTY_DEAL_TYPES)[number]

export type PropertyStatus =
  | 'DRAFT'
  | 'IN_REVIEW'
  | 'ACTIVE'
  | 'RESERVED'
  | 'SOLD'
  | 'RENTED'
  | 'INACTIVE'
  | 'ARCHIVED'

export const PROPERTY_CONDITIONS = [
  'NEWBUILDING',
  'PARTIAL_RECONSTRUCTION',
  'COMPLETE_RECONSTRUCTION',
  'ORIGINAL_CONDITION',
  'UNDER_CONSTRUCTION',
  'DEVELOP_PROJECT',
  'INTENDED_TO_DEMOLITION',
] as const

export type PropertyCondition = (typeof PROPERTY_CONDITIONS)[number]

export type PropertyOwnership =
  | 'PERSONAL'
  | 'COOPERATIVE'
  | 'COMPANY'
  | 'STATE'
  | 'MUNICIPAL'
  | 'UNSETTLED'
  | 'OTHER'

export type PropertyEnergyClass = 'A0' | 'A1' | 'A2' | 'A3' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G'

/**
 * @deprecated Formulárové pole `heating` (single-select palivo) nahradil
 * viacnásobný výber `heatingSystems: UcHeatingSystem[]` — vlna 3 UC exportu ho
 * odstráni z admin/mobile formulárov; `PropertyDto.heating` je preto už
 * odstránené. Tento typ ostáva NATRVALO (nie dočasne) len pre historické
 * revízie: `property_revisions` obsahujú staré `heating` diffy a
 * `PropertyHistoryPanel` ich renderuje cez `PROPERTY_HEATING_LABELS` a
 * `PROPERTY_FIELD_LABELS.heating`.
 */
export type PropertyHeating =
  | 'GAS'
  | 'ELECTRIC'
  | 'SOLID_FUEL'
  | 'DISTRICT'
  | 'HEAT_PUMP'
  | 'SOLAR'
  | 'OTHER'

export type MediaType = 'PHOTO' | 'VIDEO' | 'FLOOR_PLAN' | 'DOCUMENT' | 'VIRTUAL_TOUR'

export interface PropertyMediaDto {
  id: string
  type: MediaType
  url: string
  thumbnailUrl?: string | null
  title?: string | null
  altText?: string | null
  sortOrder: number
  isCover: boolean
  width?: number | null
  height?: number | null
}

export interface PropertyLocationDto {
  street?: string | null
  streetNumber?: string | null
  city?: string | null
  cityPart?: string | null
  district?: string | null
  region?: string | null
  country: string
  zip?: string | null
  lat?: number | null
  lng?: number | null
  locationHidden: boolean
}

export interface PropertyFeaturesDto {
  hasBalcony: boolean
  hasTerrace: boolean
  hasLoggia: boolean
  hasCellar: boolean
  hasStorageRoom: boolean
  hasParking: boolean
  hasGarage: boolean
  hasElevator: boolean
  hasPool: boolean
  hasSauna: boolean
  hasSecurity: boolean
  hasSmartHome: boolean
  isBarrierFree: boolean
  hasGarden: boolean
}

export interface PropertyDto {
  id: number
  tenantId: string
  branchId?: string | null
  agentId: string
  transactionType: PropertyTransactionType
  propertyType: PropertyType
  status: PropertyStatus

  title: string
  description?: string | null

  price?: number | null
  priceNegotiable: boolean
  priceHidden: boolean
  rentPrice?: number | null
  monthlyCharges?: number | null
  currency: string

  location: PropertyLocationDto
  features: PropertyFeaturesDto

  floorArea?: number | null
  usableArea?: number | null
  landArea?: number | null
  roomsCount?: number | null
  bedroomsCount?: number | null
  bathroomsCount?: number | null
  floor?: number | null
  totalFloors?: number | null

  condition?: PropertyCondition | null
  ownership?: PropertyOwnership | null
  constructionYear?: number | null
  energyClass?: PropertyEnergyClass | null

  propertySubtype?: PropertySubtype | null
  subtypeNeedsReview: boolean

  equipType?: UcEquipType | null
  heatingSystems: UcHeatingSystem[]
  position?: UcPosition | null
  territory?: UcTerritory | null
  typeOfHouse?: UcHouseType | null
  groundType?: UcGroundType | null
  readyToBuild?: UcReadyToBuild | null
  orientation: UcOrientation[]
  buildingType: UcBuildingType[]
  typeOfParking: UcParkingType[]
  utilitiesWater: UcWater[]
  utilitiesElectric: UcElectric[]
  utilitiesSewage: UcSewage[]
  utilitiesGas: UcGas[]

  guarantee?: number | null
  powerCosts?: number | null
  powerCostsInPrice: boolean

  ucRegionId?: number | null
  ucCitypartId?: number | null
  ucStreetId?: number | null

  dealType: PropertyDealType
  mortgagePossible: boolean
  availableFrom?: Date | null

  media: PropertyMediaDto[]
  viewCount: number
  publishedAt?: Date | null
  createdAt: Date
  updatedAt: Date
}

export interface PropertyListItemDto {
  id: number
  transactionType: PropertyTransactionType
  propertyType: PropertyType
  propertySubtype?: PropertySubtype | null
  status: PropertyStatus
  title: string
  price?: number | null
  priceHidden: boolean
  currency: string
  city?: string | null
  district?: string | null
  floorArea?: number | null
  roomsCount?: number | null
  coverImage?: string | null
  dealType: PropertyDealType
  publishedAt?: Date | null
  agentId: string
  branchId?: string | null
  /** Meno hlavného vlastníka (isPrimary, fallback prvý) — picker nehnuteľnosti pri udalosti. */
  primaryOwnerName?: string | null
  createdAt: Date
  updatedAt: Date
}

/**
 * Row shape returned by the admin GET /properties list. Distinct from the
 * catalog-facing `PropertyListItemDto` above — this matches the trimmed payload
 * the admin table actually renders (publishedPortals, soft-delete, agent name).
 */
export interface PropertyListItem {
  id: number
  title: string
  seoSlug: string | null
  status: string
  propertyType: string
  /** Vracia ho `findAll` select — Export Center ukazuje odhadnutý podtyp na dotriedenie. */
  propertySubtype?: string | null
  transactionType: string
  /** Typ ponuky (výhradná / nevýhradná / podpultová) — zoznam ukazuje odznak pri výhradnej a podpultovej. */
  dealType: PropertyDealType
  price: number
  currency: string
  city: string
  floorArea: number | null
  coverImage: string | null
  /** Miniatúra 400×300 titulnej fotky (bez nej `coverImage`) — plná fotka má až 4096 px a ~3,5 MB, v tabuľke laguje scroll. */
  coverThumbnail: string | null
  photoCount: number
  publishedPortals: string[]
  createdAt: string
  deletedAt: string | null
  branchId: string | null
  agent: { id: string; firstName: string; lastName: string; agentSlug?: string | null } | null
}

/** Vlastník nehnuteľnosti v detaile — osoba typu Záujemca. */
export interface PropertyOwnerPerson {
  id: string
  firstName: string
  lastName: string
  email?: string | null
  phone?: string | null
  company?: string | null
}

/**
 * Detail payload returned by GET /properties/:id, as consumed by the admin
 * edit form. The form maps the bulk of the body generically into RHF state, so
 * only the fields read in a typed position are spelled out; the index signature
 * keeps the generic `property[key]` access well-typed without enumerating every
 * column.
 */
export interface PropertyDetailPayload {
  id: number
  agentId?: string | null
  branchId?: string | null
  price?: number | null
  commissionAmount?: number | null
  deletedAt?: string | null
  publishedPortals?: string[]
  owners?: Array<{
    contact: PropertyOwnerPerson
    ownershipShare: number | string
    isPrimary: boolean
  }>
  [key: string]: unknown
}

// ── História zmien (property_revisions) ────────────────────────────────────

export type PropertyRevisionAction = 'CREATE' | 'UPDATE' | 'RESTORE' | 'MEDIA'

export interface PropertyRevisionChange {
  from: unknown
  to: unknown
}

/** Typ média v MEDIA revízii — zhoduje sa s Prisma enumom MediaType. */
export type PropertyMediaRevisionKind =
  | 'PHOTO'
  | 'VIDEO'
  | 'DOCUMENT'
  | 'FLOOR_PLAN'
  | 'VIRTUAL_TOUR'

/**
 * Zmena médií (foto/video/dokument) v jednej MEDIA revízii — len súhrn (počty),
 * nie per-súbor detail. `op` = druh operácie; `mediaType`/`count` platia pre
 * ADD/REMOVE (batch upload = jeden záznam s count = počet súborov).
 */
export interface PropertyMediaChange {
  op: 'ADD' | 'REMOVE' | 'REORDER' | 'COVER'
  mediaType?: PropertyMediaRevisionKind
  count?: number
}

export interface PropertyRevisionDto {
  id: string
  action: PropertyRevisionAction
  /**
   * Zmenené polia { pole: { from, to } } pri UPDATE/RESTORE, null pri CREATE,
   * a { media: {...} } pri MEDIA zázname (foto/video/dokument zmeny).
   */
  diff: Record<string, PropertyRevisionChange> | { media: PropertyMediaChange } | null
  createdAt: string
  changedBy: { id: string; firstName: string; lastName: string } | null
}

/**
 * Payload GET /properties/:id/history. Zámerne bez kľúča `data` —
 * unwrapApiEnvelope by `{ data, refs, canRestore }` skolaboval na samotné
 * pole revízií a refs/canRestore by sa stratili (pozri api/envelope.ts).
 */
export interface PropertyRevisionListPayload {
  revisions: PropertyRevisionDto[]
  /** Či smie aktuálny používateľ obnovovať verzie (canWriteProperty na API). */
  canRestore: boolean
  /** Mená pre UUID hodnoty v diffoch (agentId → meno makléra, branchId → názov pobočky, coverOverlay → názov coveru). */
  refs: {
    users: Record<string, string>
    branches: Record<string, string>
    coverOverlays?: Record<string, string>
  }
}

// Filtračné parametre pre katalóg
export interface PropertyFilters {
  transactionType?: PropertyTransactionType
  propertyType?: PropertyType | PropertyType[]
  status?: PropertyStatus | PropertyStatus[]
  city?: string | string[]
  district?: string | string[]
  region?: string | string[]
  priceMin?: number
  priceMax?: number
  floorAreaMin?: number
  floorAreaMax?: number
  roomsCountMin?: number
  roomsCountMax?: number
  floor?: number
  condition?: PropertyCondition | PropertyCondition[]
  energyClass?: PropertyEnergyClass | PropertyEnergyClass[]
  hasBalcony?: boolean
  hasTerrace?: boolean
  hasParking?: boolean
  hasGarage?: boolean
  hasElevator?: boolean
  isBarrierFree?: boolean
  agentId?: string
  branchId?: string
  search?: string
}

// ─── Cover titulnej fotky (cover overlay) ────────────────────────────────────
// Obrázok (typicky PNG s priehľadnosťou), ktorý sa serverovo zapečie do titulnej
// fotky — zložená fotka je potom skutočná titulná (odchádza aj na portály).
// Pozor na názvoslovie: `PropertyMedia.isCover` = titulná fotka, toto je „cover overlay".

// Covery sú PER MAKLÉR; do titulnej fotky sa zapeká cover makléra nehnuteľnosti (agentId).
// `Property.coverOverlay` drží ID coveru: štartovacie sloty majú pevné ID (EMPTY…),
// ďalšie covery, ktoré si maklér pridá, majú UUID.

/** Štartovacie sloty — každý maklér ich má vždy; dajú sa premenovať a vyprázdniť, nie zmazať. */
export const STARTER_COVER_OVERLAY_IDS = ['EMPTY', 'PREPARING', 'RESERVED', 'SOLD'] as const
export type StarterCoverOverlayId = (typeof STARTER_COVER_OVERLAY_IDS)[number]

/** Predvolené názvy štartovacích slotov (kým ich maklér nepremenuje). */
export const STARTER_COVER_OVERLAY_NAMES: Record<StarterCoverOverlayId, string> = {
  EMPTY: 'Prázdny',
  PREPARING: 'Pripravujem',
  RESERVED: 'Rezervované',
  SOLD: 'Predané',
}

/** Strop coverov na makléra (vrátane štartovacích slotov). */
export const MAX_COVER_OVERLAYS = 30
export const MAX_COVER_OVERLAY_NAME_LENGTH = 40

/** Cover makléra, ako ho vracia `GET /users/:id/cover-overlays/list` (štartovacie sloty vždy). */
export interface CoverOverlay {
  id: string
  name: string
  /** null = obrázok nenahratý (môže byť len štartovací slot). */
  url: string | null
  starter: boolean
}

export function isStarterCoverOverlayId(id: string): id is StarterCoverOverlayId {
  return (STARTER_COVER_OVERLAY_IDS as readonly string[]).includes(id)
}

/** Kľúč na porovnanie názvov coverov — bez diakritiky, veľkých písmen a zdvojených medzier. */
export function normalizeCoverOverlayName(name: string): string {
  return name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim()
}

/**
 * Cover nehnuteľnosti po zmene makléra: cover nového makléra s rovnakým názvom
 * (normalizeCoverOverlayName), inak žiadny. Rovnaké pravidlo na API aj vo formulároch.
 */
export function mapCoverOverlayToAgent(
  fromCovers: readonly CoverOverlay[],
  coverId: string | null | undefined,
  toCovers: readonly CoverOverlay[],
): string | null {
  if (!coverId) return null
  const source = fromCovers.find((c) => c.id === coverId)
  if (!source) return null
  const key = normalizeCoverOverlayName(source.name)
  return toCovers.find((c) => normalizeCoverOverlayName(c.name) === key)?.id ?? null
}
